// Phase 16.2 — Clean, minimal, interactive 4-node Lifted Ecosystem diagram.
//
// Replaces the prior "five-planet orbital" diagram. Layout per directive:
//   - Affiliates at TOP
//   - Drivers at RIGHT
//   - Partners at BOTTOM
//   - Franchise at LEFT
//   - Center pill reads "LIFTED ECOSYSTEM"
//   - Purple/black cosmic backdrop, neon-green orbit + glow
//   - Clicking a node reveals its role breakdown (no router nav)
//
// Note: "Content Creator" is intentionally NOT a separate role — content
// creation is a function of the Affiliate role.

import React, { useState } from "react";
import { Megaphone, Truck, Store, Building2, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/retention";

const NODES = [
  {
    key: "affiliate",
    label: "Affiliates",
    angle: -90,           // top
    icon: Megaphone,
    cta: { label: "Become an affiliate", to: "/affiliate-zone" },
    breakdown: {
      title: "AFFILIATES — THE HEARTBEAT",
      bullets: [
        "Affiliates are the heartbeat of the ecosystem.",
        "Affiliates promote Lifted products and brand experiences.",
        "Affiliates create content and word-of-mouth visibility.",
        "Affiliates share tracked links and unique referral codes.",
        "Affiliates can refer customers, drivers, partner stores, and franchise candidates.",
        "Training unlocks after approval / activation.",
        "No guaranteed earnings.",
      ],
    },
  },
  {
    key: "driver",
    label: "Drivers",
    angle: 0,             // right
    icon: Truck,
    cta: { label: "Drive for Lifted", to: "/delivery" },
    breakdown: {
      title: "DRIVERS — LAST-MILE LIFT",
      bullets: [
        "Drivers support Lifted Supplies same-day delivery.",
        "Drivers pick up and deliver approved orders.",
        "Drivers must be approved and screened before going active.",
        "Driver access is not automatic.",
        "Live map tools are prepared and show a fallback until the map API is connected.",
      ],
    },
  },
  {
    key: "partner",
    label: "Partners",
    angle: 90,            // bottom
    icon: Store,
    cta: { label: "Partner with us", to: "/partners" },
    breakdown: {
      title: "PARTNERS — STOREFRONT ALLIES",
      bullets: [
        "Partner stores plug into the Lifted ecosystem.",
        "Partners may upload approved products to their storefront.",
        "Partners can gain visibility from affiliate-driven content and customer traffic.",
        "Partner products appear once approved by the Lifted team.",
        "Partner approval is required.",
      ],
    },
  },
  {
    key: "franchise",
    label: "Franchise",
    angle: 180,           // left
    icon: Building2,
    cta: { label: "Explore franchise", to: "/franchise" },
    breakdown: {
      title: "FRANCHISE — LONG-TERM GROWTH",
      bullets: [
        "Franchisees represent long-term Lifted territory growth.",
        "Franchisees help organize stores, drivers, customers, and affiliate activity in a market.",
        "Franchise approval is required.",
        "No business outcome is guaranteed.",
      ],
    },
  },
];

const RADIUS = 175;
const CENTER = 250;

export default function OrbitalEcosystem({ id = "orbital-ecosystem" }) {
  const [active, setActive] = useState(null);

  const onSelect = (node) => {
    setActive(node);
    trackEvent("ecosystem_role_click", { surface: "diagram", role: node.key });
  };

  return (
    <section
      id={id}
      data-testid={id}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Diagram */}
        <div className="lg:col-span-7 flex justify-center">
          <div className="relative w-full max-w-[520px] aspect-square">
            {/* Cosmic backdrop */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(184,255,103,0.18) 0%, rgba(139,63,194,0.18) 38%, rgba(11,11,16,0) 72%)",
              }}
              aria-hidden="true"
            />
            <svg viewBox="0 0 500 500" className="absolute inset-0 w-full h-full" aria-hidden="true">
              <defs>
                <radialGradient id="centerGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#0B0B10" />
                  <stop offset="100%" stopColor="#15131D" />
                </radialGradient>
                <filter id="nodeGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Orbit ring */}
              <circle
                cx={CENTER}
                cy={CENTER}
                r={RADIUS}
                fill="none"
                stroke="#B8FF67"
                strokeOpacity="0.35"
                strokeWidth="1"
                strokeDasharray="2 6"
              />
              {/* Outer faint ring */}
              <circle
                cx={CENTER}
                cy={CENTER}
                r={RADIUS + 22}
                fill="none"
                stroke="#8B3FC2"
                strokeOpacity="0.18"
                strokeWidth="0.75"
              />

              {/* Connection spokes (subtle) */}
              {NODES.map((n) => {
                const rad = (n.angle * Math.PI) / 180;
                const x = CENTER + Math.cos(rad) * RADIUS;
                const y = CENTER + Math.sin(rad) * RADIUS;
                return (
                  <line
                    key={`spoke-${n.key}`}
                    x1={CENTER}
                    y1={CENTER}
                    x2={x}
                    y2={y}
                    stroke="#B8FF67"
                    strokeOpacity={active?.key === n.key ? 0.55 : 0.12}
                    strokeWidth="0.8"
                    strokeDasharray="3 5"
                  />
                );
              })}

              {/* Center pill */}
              <g filter="url(#nodeGlow)">
                <ellipse cx={CENTER} cy={CENTER} rx={92} ry={42} fill="url(#centerGrad)" stroke="#B8FF67" strokeWidth="1.2" />
              </g>
              <text x={CENTER} y={CENTER - 4} textAnchor="middle" fontFamily="Anton, Bebas Neue, sans-serif"
                    fontSize="14" letterSpacing="2.5" fill="#F3F5F0">LIFTED</text>
              <text x={CENTER} y={CENTER + 14} textAnchor="middle" fontFamily="Anton, Bebas Neue, sans-serif"
                    fontSize="11" letterSpacing="2.2" fill="#B8FF67">ECOSYSTEM</text>

              {/* Role nodes */}
              {NODES.map((n) => {
                const rad = (n.angle * Math.PI) / 180;
                const x = CENTER + Math.cos(rad) * RADIUS;
                const y = CENTER + Math.sin(rad) * RADIUS;
                const isActive = active?.key === n.key;
                return (
                  <g
                    key={n.key}
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelect(n)}
                    data-testid={`ecosystem-node-${n.key}`}
                  >
                    {/* halo */}
                    <circle cx={x} cy={y} r={isActive ? 42 : 36} fill="#B8FF67" fillOpacity={isActive ? 0.22 : 0.10} />
                    {/* circle body */}
                    <circle
                      cx={x}
                      cy={y}
                      r={26}
                      fill="#0B0B10"
                      stroke={isActive ? "#B8FF67" : "#8B3FC2"}
                      strokeWidth={isActive ? 1.6 : 1}
                    />
                    {/* Centered label below */}
                    <text
                      x={x}
                      y={y + 50}
                      textAnchor="middle"
                      fontFamily="Anton, Bebas Neue, sans-serif"
                      fontSize="11"
                      letterSpacing="2"
                      fill={isActive ? "#B8FF67" : "#F3F5F0"}
                    >
                      {n.label.toUpperCase()}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Icon overlays placed exactly on each node */}
            {NODES.map((n) => {
              const rad = (n.angle * Math.PI) / 180;
              // Translate from SVG coords (500x500) to the responsive container.
              const xPct = ((CENTER + Math.cos(rad) * RADIUS) / 500) * 100;
              const yPct = ((CENTER + Math.sin(rad) * RADIUS) / 500) * 100;
              const Icon = n.icon;
              const isActive = active?.key === n.key;
              return (
                <button
                  type="button"
                  key={`icon-${n.key}`}
                  onClick={() => onSelect(n)}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full grid place-items-center transition ${
                    isActive ? "scale-110" : "hover:scale-105"
                  }`}
                  style={{ left: `${xPct}%`, top: `${yPct}%` }}
                  aria-label={n.label}
                  data-testid={`ecosystem-node-icon-${n.key}`}
                >
                  <Icon className={`h-4 w-4 ${isActive ? "text-lifted-green" : "text-white/80"}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail / role breakdown */}
        <div className="lg:col-span-5">
          {active ? (
            <RoleBreakdown node={active} onClose={() => setActive(null)} />
          ) : (
            <Intro nodes={NODES} onSelect={onSelect} />
          )}
        </div>
      </div>
    </section>
  );
}

function Intro({ nodes, onSelect }) {
  return (
    <div>
      <div className="eyebrow">The Lifted Ecosystem</div>
      <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-3 leading-[0.95]">
        Four roles. <span className="text-lifted-green">One orbit.</span>
      </h2>
      <p className="text-lifted-dim text-sm mt-4 max-w-md">
        Lifted at the center. Affiliates, Drivers, Partners and Franchise orbit it — each
        amplifying the others. Tap any role to see how it fits.
      </p>
      <div className="mt-5 grid grid-cols-2 gap-2" data-testid="ecosystem-node-list">
        {nodes.map((n) => {
          const Icon = n.icon;
          return (
            <button
              type="button"
              key={n.key}
              onClick={() => onSelect(n)}
              className="glass-panel p-3 flex items-center gap-2 hover:-translate-y-0.5 transition-transform text-left"
              data-testid={`ecosystem-quick-${n.key}`}
            >
              <Icon className="h-4 w-4 text-lifted-green" />
              <span className="font-anton uppercase text-[0.7rem]">{n.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RoleBreakdown({ node, onClose }) {
  const Icon = node.icon;
  return (
    <div
      className="glass-panel p-6 border border-lifted-green/30"
      data-testid={`ecosystem-breakdown-${node.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-lifted-green" />
          <div className="font-bebas tracking-[0.22em] text-sm text-white">
            {node.breakdown.title}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-lifted-dim hover:text-lifted-text"
          aria-label="Close role breakdown"
          data-testid={`ecosystem-breakdown-close-${node.key}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="mt-4 space-y-2 text-sm text-lifted-text leading-relaxed">
        {node.breakdown.bullets.map((b, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-lifted-green mt-1">•</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {node.cta && (
        <Link
          to={node.cta.to}
          className="mt-5 inline-flex items-center gap-2 btn-lifted !py-2 !px-4 text-xs"
          data-testid={`ecosystem-breakdown-cta-${node.key}`}
        >
          {node.cta.label} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
      <div className="mt-4 text-[0.65rem] text-lifted-dim">
        Disclaimer: Lifted At The Moment makes no guarantee of earnings or business outcomes.
      </div>
    </div>
  );
}
